import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  Bot, TrendingUp, ArrowLeft, Info, CheckCircle, AlertCircle, 
  Clock, Send, ArrowUpRight, ArrowDownRight, Copy, Check,
  ChevronLeft, ChevronRight, Filter, Search, Calendar, X,
  Rocket, Zap, Shield, DollarSign, Users, Activity,
  Play, Pause, RefreshCw, Settings
} from 'lucide-react';
import api from '../services/api';
import axios from 'axios';
import PortfolioChart from './PortfolioChart';

function IPTokenDashboard() {
  const { tokenId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [tokenData, setTokenData] = useState(location.state?.tokenData || null);
  const [activeTab, setActiveTab] = useState('guide');
  const [distributionConfig, setDistributionConfig] = useState(null);
  const [distributionLoading, setDistributionLoading] = useState(false);
  const [distributionFormData, setDistributionFormData] = useState({
    // Pre-breakeven percentages
    preBreakevenRewardPercentage: 70,
    preBreakevenIpOwnerPercentage: 27,
    preBreakevenPlatformPercentage: 3,
    // Post-breakeven percentages  
    postBreakevenRewardPercentage: 30,
    postBreakevenIpOwnerPercentage: 63,
    postBreakevenPlatformPercentage: 7,
    ipOwnerEmail: '',
    ipOwnerAddress: '',
    ipOwnerName: ''
  });
  const [searchingIpOwner, setSearchingIpOwner] = useState(false);
  const [showNoIpModal, setShowNoIpModal] = useState(false);
  const [noIpModalData, setNoIpModalData] = useState({ email: '', token: '' });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [weeklyPnl, setWeeklyPnl] = useState(null);
  const [distributionInfo, setDistributionInfo] = useState(null);
  const [distributionHistory, setDistributionHistory] = useState([]);
  const [cumulativeProfit, setCumulativeProfit] = useState(0);
  const [profitLoading, setProfitLoading] = useState(false);
  const [pnlLoading, setPnlLoading] = useState(false);
  const [loading, setLoading] = useState(!tokenData);
  const [walletBalance, setWalletBalance] = useState({
    wallet_address: null,
    balance: 0,
    balance_usd: 0,
    last_updated: null
  });
  const [walletBalanceLoading, setWalletBalanceLoading] = useState(true);
  const [rewardWalletBalance, setRewardWalletBalance] = useState({
    wallet_address: null,
    balance: 0,
    balance_usd: 0,
    last_updated: null
  });
  const [rewardWalletLoading, setRewardWalletLoading] = useState(true);
  const [assetOverview, setAssetOverview] = useState({
    assets: [],
    totalValueUsd: 0,
    walletCount: 0
  });
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [copiedMint, setCopiedMint] = useState(null);
  const [copiedWallet, setCopiedWallet] = useState(null);
  const [operations, setOperations] = useState([]);
  const [operationsLoading, setOperationsLoading] = useState(true);
  
  // Operations sub-tab state
  const [operationsSubTab, setOperationsSubTab] = useState('commands'); // 'commands' or 'trades'
  
  // Autopilot state
  const [autopilotStatus, setAutopilotStatus] = useState(null);
  const [autopilotConditions, setAutopilotConditions] = useState(null);
  const [checkingConditions, setCheckingConditions] = useState(true); // Start as true so it shows "Checking..." initially
  const [executingAutopilot, setExecutingAutopilot] = useState(false);
  const [showAutopilotModal, setShowAutopilotModal] = useState(false);
  const [autopilotProgress, setAutopilotProgress] = useState({
    step: '',
    message: '',
    progress: 0,
    details: {}
  });
  const [autopilotAgent, setAutopilotAgent] = useState(null);
  const autopilotCheckInterval = useRef(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showRequirementsModal, setShowRequirementsModal] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [requirementsData, setRequirementsData] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showTestingMode, setShowTestingMode] = useState(false);
  
  // Pagination and filtering state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalOperations, setTotalOperations] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Helper function to format very small numbers
  const formatPrice = (price) => {
    if (!price || price === 0) return '$0.00';
    
    // For prices above 0.01, use standard formatting
    if (price >= 0.01) {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }
    
    // For very small prices, show more decimals
    if (price >= 0.000001) {
      return `$${price.toFixed(6)}`;
    }
    
    // For extremely small prices, show up to 10 decimals
    if (price >= 0.0000000001) {
      return `$${price.toFixed(10)}`;
    }
    
    // For anything smaller, just show the actual number
    return `$${price.toFixed(12)}`;
  };

  // Helper function to copy mint address
  const copyMintAddress = (mint, symbol) => {
    if (!mint) return;
    
    navigator.clipboard.writeText(mint);
    setCopiedMint(symbol);
    
    // Reset after 2 seconds
    setTimeout(() => {
      setCopiedMint(null);
    }, 2000);
  };

  // Helper function to copy wallet address
  const copyWalletAddress = (address, walletType) => {
    if (!address) return;
    
    navigator.clipboard.writeText(address);
    setCopiedWallet(walletType);
    
    // Reset after 2 seconds
    setTimeout(() => {
      setCopiedWallet(null);
    }, 2000);
  };


  useEffect(() => {
    if (tokenId) {
      // Always fetch token data to get market data, even if basic token info exists
      fetchTokenData();
      fetchWalletBalance();
      fetchRewardWalletBalance();
      fetchAssetOverview();
      fetchDistributionConfig();
      fetchWeeklyPnl();
      fetchOperations();
      fetchIpOwnerFromIpos();
      fetchCumulativeProfit();
      checkAutopilotStatus();
      // Delay checking conditions to ensure tokenId is ready
      setTimeout(() => {
        checkAutopilotConditions();
      }, 1000);

      // Removed automatic refresh - portfolio will only load once on page load
      // If you need auto-refresh later, uncomment the code below:
      // const assetRefreshInterval = setInterval(() => {
      //   fetchAssetOverview();
      // }, 10000); // Refresh every 10 seconds
      //
      // return () => {
      //   clearInterval(assetRefreshInterval);
      // };
    }
  }, [tokenId]);
  
  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (autopilotCheckInterval.current) {
        clearInterval(autopilotCheckInterval.current);
      }
    };
  }, []);

  // Refetch operations when filters change
  useEffect(() => {
    if (tokenId && activeTab === 'operations') {
      fetchOperations(1, true);
    }
  }, [typeFilter, statusFilter, dateFilter, customDateFrom, customDateTo, itemsPerPage, operationsSubTab]);

  // Refetch operations when page changes
  useEffect(() => {
    if (tokenId && activeTab === 'operations') {
      fetchOperations(currentPage);
    }
  }, [currentPage]);

  // Search with debounce
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (tokenId && activeTab === 'operations') {
        fetchOperations(1, true);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Helper functions for pagination
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('all');
    setStatusFilter('all');
    setDateFilter('all');
    setCustomDateFrom('');
    setCustomDateTo('');
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const generatePageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const fetchTokenData = async () => {
    try {
      // Try both endpoints - first the available-tokens endpoint, then the tokens endpoint
      let response;
      try {
        response = await api.get(`/available-tokens/${tokenId}`);
      } catch (error) {
        console.log('Available tokens endpoint failed, trying tokens endpoint:', error.message);
        response = await api.get(`/tokens/${tokenId}`);
      }
      
      console.log('Token data fetched successfully:', response.data);
      setTokenData(response.data);
    } catch (error) {
      console.error('Error fetching token data:', error);
      // Use mock data for demo
      setTokenData({
        id: tokenId,
        token_id: tokenId,
        token_name: 'Demo Token',
        token_symbol: 'DEMO',
        token_image: '🐕',
        name: 'Demo Token',
        symbol: 'DEMO',
        image: '🐕'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      setWalletBalanceLoading(true);
      const response = await api.get(`/user/token-wallet-balance/${tokenId}`);
      setWalletBalance(response.data);
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
      // Set empty data if API fails
      setWalletBalance({
        wallet_address: null,
        balance: 0,
        balance_usd: 0,
        last_updated: null
      });
    } finally {
      setWalletBalanceLoading(false);
    }
  };

  const fetchRewardWalletBalance = async () => {
    try {
      setRewardWalletLoading(true);
      const response = await api.get(`/user/reward-wallet-balance/${tokenId}`);
      setRewardWalletBalance(response.data);
    } catch (error) {
      console.error('Error fetching reward wallet balance:', error);
      // Set empty data if API fails
      setRewardWalletBalance({
        wallet_address: null,
        balance: 0,
        balance_usd: 0,
        last_updated: null
      });
    } finally {
      setRewardWalletLoading(false);
    }
  };

  const fetchAssetOverview = async () => {
    try {
      setAssetsLoading(true);
      const response = await api.get(`/asset-overview/${tokenId}`);
      console.log('Asset overview response:', response.data);
      setAssetOverview(response.data);
    } catch (error) {
      console.error('Error fetching asset overview:', error);
      // Set empty data if API fails
      setAssetOverview({
        assets: [],
        totalValueUsd: 0,
        walletCount: 0
      });
    } finally {
      setAssetsLoading(false);
    }
  };

  const fetchDistributionConfig = async () => {
    try {
      const response = await api.get(`/distribution/token/${tokenId}`);
      setDistributionConfig(response.data);
      
      // Update form data if config exists
      if (response.data.exists && response.data.config) {
        setDistributionFormData(prev => ({
          ...prev,
          // Pre-breakeven percentages - use existing config or defaults
          preBreakevenRewardPercentage: parseFloat(response.data.config.preBreakevenRewardPercentage) || 70,
          preBreakevenIpOwnerPercentage: parseFloat(response.data.config.preBreakevenIpOwnerPercentage) || 27,
          preBreakevenPlatformPercentage: parseFloat(response.data.config.preBreakevenPlatformPercentage) || 3,
          // Post-breakeven percentages - use existing config or defaults
          postBreakevenRewardPercentage: parseFloat(response.data.config.postBreakevenRewardPercentage) || 30,
          postBreakevenIpOwnerPercentage: parseFloat(response.data.config.postBreakevenIpOwnerPercentage) || 63,
          postBreakevenPlatformPercentage: parseFloat(response.data.config.postBreakevenPlatformPercentage) || 7,
          ipOwnerEmail: response.data.config.ipOwnerEmail || '',
          ipOwnerAddress: response.data.config.ipOwnerAddress || '',
          ipOwnerName: response.data.config.ipOwnerName || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching distribution config:', error);
    }
  };

  const fetchWeeklyPnl = async () => {
    try {
      setPnlLoading(true);
      const response = await api.get(`/distribution/weekly-pnl/${tokenId}`);
      setWeeklyPnl(response.data);
    } catch (error) {
      console.error('Error fetching weekly PnL:', error);
    } finally {
      setPnlLoading(false);
    }
  };

  // Fetch IP owner email from IPOS route
  const fetchIpOwnerFromIpos = async () => {
    try {
      const response = await api.get(`/ipos/${tokenId}`);
      if (response.data && response.data.ip_owner_email) {
        setDistributionFormData(prev => ({
          ...prev,
          ipOwnerEmail: response.data.ip_owner_email
        }));
        // Also search for their address
        await searchIpOwnerByEmail(response.data.ip_owner_email, false);
      }
    } catch (error) {
      console.error('Error fetching IP owner from IPOS:', error);
    }
  };

  // Fetch cumulative distributable profit for the week
  const fetchCumulativeProfit = async () => {
    try {
      setProfitLoading(true);
      // This would call your calculation profit route to get weekly cumulative data
      const response = await api.get(`/weekly-distribution/cumulative-profit/${tokenId}`);
      setCumulativeProfit(response.data.cumulative_profit || 0);
    } catch (error) {
      console.error('Error fetching cumulative profit:', error);
      setCumulativeProfit(0);
    } finally {
      setProfitLoading(false);
    }
  };

  const searchIpOwnerByEmail = async (email, showModal = true) => {
    if (!email || !email.includes('@')) return;
    
    setSearchingIpOwner(true);
    try {
      const response = await axios.get('https://app.giggle.pro/api/v1/ip-library', {
        params: {
          page: 1,
          page_size: 10,
          email: email,
          launched_to_giggle: true
        }
      });
      
      if (response.data?.data?.data && response.data.data.data.length > 0) {
        // Find the IP that matches the current token
        const ipData = response.data.data.data.find(ip => 
          ip.token_info?.mint === tokenId || ip.ticker === tokenId || ip.symbol === tokenId
        );
        
        if (ipData && ipData.token_info?.user_address) {
          setDistributionFormData(prev => ({
            ...prev,
            ipOwnerAddress: ipData.token_info.user_address,
            ipOwnerName: ipData.creator || ipData.name || ''
          }));
        } else {
          // If no exact match, try to get the first one's address
          const firstIp = response.data.data.data[0];
          if (firstIp && firstIp.token_info?.user_address) {
            setDistributionFormData(prev => ({
              ...prev,
              ipOwnerAddress: firstIp.token_info.user_address,
              ipOwnerName: firstIp.creator || firstIp.name || ''
            }));
          } else {
            // No valid IP found, show modal only if requested and clear the fields
            console.log('No valid IP found for email:', email, 'token:', tokenId);
            if (showModal) {
              const modalData = { email, token: tokenData?.name || tokenData?.ticker || tokenId };
              console.log('Setting modal data:', modalData);
              setNoIpModalData(modalData);
              setShowNoIpModal(true);
              console.log('Modal should now be visible');
            }
            setDistributionFormData(prev => ({
              ...prev,
              ipOwnerAddress: '',
              ipOwnerName: ''
            }));
          }
        }
      } else {
        // No IPs found for this email, show modal only if requested and clear the fields
        console.log('No IPs found for email:', email, 'token:', tokenId);
        if (showModal) {
          setNoIpModalData({ email, token: tokenData?.name || tokenData?.ticker || tokenId });
          setShowNoIpModal(true);
        }
        setDistributionFormData(prev => ({
          ...prev,
          ipOwnerAddress: '',
          ipOwnerName: ''
        }));
      }
    } catch (error) {
      console.error('Error searching IP owner:', error);
      // On error, clear the fields
      setDistributionFormData(prev => ({
        ...prev,
        ipOwnerAddress: '',
        ipOwnerName: ''
      }));
    } finally {
      setSearchingIpOwner(false);
    }
  };

  const fetchOperations = async (page = currentPage, resetPage = false) => {
    try {
      setOperationsLoading(true);
      
      if (resetPage) {
        page = 1;
        setCurrentPage(1);
      }
      
      // Build query parameters
      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        offset: ((page - 1) * itemsPerPage).toString()
      });
      
      // Add filters
      // If on trades sub-tab, always filter for trades only
      if (operationsSubTab === 'trades') {
        params.append('type', 'trade');
        // Add trade-specific filtering by buy/sell type in the frontend
        // Backend will return all trades, we'll filter client-side by metadata
      } else if (operationsSubTab === 'commands') {
        // On commands tab, handle filtering
        if (typeFilter && typeFilter !== 'all') {
          // If specific type selected (AI Command or Transfer), use type filter
          const typeMap = { 'AI Command': 'ai_command', 'Transfer': 'transfer' };
          params.append('type', typeMap[typeFilter] || typeFilter);
          // Don't need exclude_type since we're already filtering for specific type
        } else {
          // If showing all types in commands tab, exclude trades
          params.append('exclude_type', 'trade');
        }
      } else if (typeFilter && typeFilter !== 'all') {
        const typeMap = { 'AI Command': 'ai_command', 'Trade': 'trade', 'Transfer': 'transfer' };
        params.append('type', typeMap[typeFilter] || typeFilter);
      }
      
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter.toLowerCase());
      }
      
      // Add date filtering logic based on dateFilter
      if (dateFilter !== 'all') {
        const now = new Date();
        let fromDate = null;
        
        switch (dateFilter) {
          case 'today':
            fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            break;
          case 'week':
            fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'month':
            fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case 'quarter':
            fromDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          case 'custom':
            if (customDateFrom) fromDate = new Date(customDateFrom);
            break;
        }
        
        if (fromDate) {
          params.append('from_date', fromDate.toISOString());
        }
        
        if (dateFilter === 'custom' && customDateTo) {
          params.append('to_date', new Date(customDateTo + ' 23:59:59').toISOString());
        }
      }
      
      const response = await api.get(`/token-operations/${tokenId}?${params.toString()}`);
      console.log('Operations response:', response.data);
      console.log('First operation sample:', response.data.operations?.[0]);
      
      const ops = response.data.operations || [];
      
      // Apply client-side filtering
      let filteredOps = ops;
      
      // Apply search filtering
      if (searchTerm) {
        filteredOps = filteredOps.filter(op => 
          op.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          op.type?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      // Apply trade type filtering (buy/sell) when on trades sub-tab
      if (operationsSubTab === 'trades' && typeFilter && typeFilter !== 'all') {
        filteredOps = filteredOps.filter(op => {
          // Check metadata for trade type
          const metadata = op.metadata;
          if (metadata && typeof metadata === 'object') {
            return metadata.type === typeFilter; // 'buy' or 'sell'
          }
          // Fallback: check description for buy/sell keywords
          const description = op.description?.toLowerCase() || '';
          if (typeFilter === 'buy') {
            return description.includes('buy') && !description.includes('sell');
          } else if (typeFilter === 'sell') {
            return description.includes('sell') && !description.includes('buy');
          }
          return true;
        });
      }
      
      setOperations(filteredOps);
      setTotalOperations(response.data.totalCount || filteredOps.length);
      setTotalPages(Math.ceil((response.data.totalCount || filteredOps.length) / itemsPerPage));
      
    } catch (error) {
      console.error('Error fetching operations:', error);
      // Set empty data if API fails
      setOperations([]);
      setTotalOperations(0);
      setTotalPages(1);
    } finally {
      setOperationsLoading(false);
    }
  };

  const renderTokenImage = (image) => {
    if (!image) return <span className="text-2xl">📊</span>;
    
    // Check if it's a URL (http/https or S3 URL)
    if (image.startsWith('http') || image.startsWith('https')) {
      return (
        <img 
          src={image} 
          alt="Token" 
          className="w-8 h-8 object-cover rounded"
          onError={(e) => {
            // Fallback to emoji on error
            e.target.style.display = 'none';
            const fallback = e.target.nextSibling;
            if (fallback) fallback.style.display = 'inline';
          }}
        />
      );
    }
    
    // Check if it's a local path
    if (image.startsWith('/token-images/')) {
      return (
        <img 
          src={`${process.env.REACT_APP_API_URL}${image}`} 
          alt="Token" 
          className="w-8 h-8 object-cover rounded"
          onError={(e) => {
            e.target.style.display = 'none';
            const fallback = e.target.nextSibling;
            if (fallback) fallback.style.display = 'inline';
          }}
        />
      );
    }
    
    // Display as emoji/icon
    return <span className="text-2xl">{image}</span>;
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleGoToAITrading = () => {
    navigate(`/ai-trading/${tokenId}`);
  };

  // Autopilot functions
  const checkAutopilotStatus = async () => {
    try {
      const response = await api.get(`/autopilot/status?tokenId=${tokenId}`);
      setAutopilotStatus(response.data);
      console.log('[AUTOPILOT] Status:', response.data);
    } catch (error) {
      console.error('Error checking autopilot status:', error);
    }
  };

  const checkAutopilotConditions = async () => {
    setCheckingConditions(true);
    try {
      console.log('[AUTOPILOT] Checking conditions for token:', tokenId);
      const response = await api.post('/autopilot/check-conditions', { tokenId });
      console.log('[AUTOPILOT] Conditions response:', response.data);
      setAutopilotConditions(response.data);
      return response.data;
    } catch (error) {
      console.error('[AUTOPILOT] Error checking autopilot conditions:', error);
      // Set empty conditions object so UI doesn't show "Unknown"
      setAutopilotConditions({
        conditionsMet: false,
        details: {
          solBalance: 0,
          usdcBalance: 0,
          userTokenPercentage: 0
        }
      });
      return null;
    } finally {
      setCheckingConditions(false);
    }
  };

  const executeAutopilot = async (testingMode = false) => {
    setExecutingAutopilot(true);
    setShowAutopilotModal(true);
    
    // Simulate progress steps
    const steps = [
      { step: 'checking', message: testingMode ? 'Preparing testing mode...' : 'Verifying activation conditions...', progress: 10 },
      { step: 'wallets', message: 'Creating 10 trading wallets...', progress: 25 },
      { step: 'group', message: 'Setting up wallet group...', progress: 35 },
      { step: 'sol', message: testingMode ? 'Preparing SOL distribution...' : 'Distributing 1 SOL to wallets...', progress: 50 },
      { step: 'usdc', message: testingMode ? 'Preparing USDC distribution...' : 'Distributing USDC (80% with variance)...', progress: 65 },
      { step: 'tokens', message: testingMode ? 'Preparing token distribution...' : 'Gathering and distributing tokens...', progress: 80 },
      { step: 'agent', message: testingMode ? 'Preparing autopilot agent...' : 'Creating autopilot agent...', progress: 90 },
      { step: 'starting', message: testingMode ? 'Preparing auto-trading strategy...' : 'Starting auto-trading strategy...', progress: 95 },
      { step: 'complete', message: testingMode ? 'Testing mode completed successfully!' : 'Autopilot activated successfully!', progress: 100 }
    ];
    
    try {
      // Update progress for each step
      for (let i = 0; i < 3; i++) {
        setAutopilotProgress(steps[i]);
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Execute the actual autopilot
      console.log('[AUTOPILOT] Executing with testingMode:', testingMode);
      const response = await api.post('/autopilot/execute', { tokenId, testingMode });
      
      if (response.data.success) {
        // Continue with remaining progress steps
        for (let i = 3; i < steps.length; i++) {
          setAutopilotProgress(steps[i]);
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        setAutopilotAgent(response.data.summary);

        // Refresh autopilot status to reflect the successful execution
        await checkAutopilotStatus();

        // Always redirect to AI trading dashboard (both testing and normal mode)
        setTimeout(() => {
          navigate(`/ai-trading/${tokenId}`);
        }, 2000);
      } else {
        throw new Error(response.data.error || 'Failed to execute autopilot');
      }
    } catch (error) {
      console.error('Error executing autopilot:', error);
      setAutopilotProgress({
        step: 'error',
        message: error.response?.data?.error || 'Failed to activate autopilot',
        progress: 0,
        details: error.response?.data?.details || {}
      });
    } finally {
      setExecutingAutopilot(false);
    }
  };

  const handleAutopilotClick = async () => {
    // Check if autopilot has already been executed
    if (autopilotStatus?.hasSuccessfulExecution) {
      alert('Autopilot has already been successfully executed for this token.');
      return;
    }

    const conditions = await checkAutopilotConditions();
    if (conditions && conditions.conditionsMet) {
      // Show professional confirmation modal
      setConfirmationData({
        title: "Activate Autopilot Trading",
        message: "Ready to activate fully automated market making!",
        details: [
          "Create 10 new trading wallets",
          "Distribute 1 SOL equally across wallets", 
          "Distribute 80% of USDC with random variance",
          "Distribute tokens randomly to wallets",
          "Launch automated trading agent",
          "Begin intelligent market making"
        ],
        onConfirm: () => {
          setShowConfirmationModal(false);
          executeAutopilot();
        },
        onCancel: () => setShowConfirmationModal(false)
      });
      setShowConfirmationModal(true);
    } else {
      // Show professional requirements modal
      const details = conditions?.details || {};
      setRequirementsData({
        title: "Autopilot Requirements Not Met",
        message: "Please ensure all requirements are satisfied before activation:",
        requirements: [
          {
            item: "SOL Balance",
            current: `${details.solBalance?.toFixed(2) || 0} SOL`,
            required: "0.1 SOL",
            status: (details.solBalance || 0) >= 0.1
          },
          {
            item: "USDC Balance",
            current: `$${details.usdcBalance?.toLocaleString() || 0}`,
            required: "$80,000",
            status: (details.usdcBalance || 0) >= 80000
          },
          {
            item: "Token Ownership",
            current: `${details.userTokenPercentage?.toFixed(1) || 0}%`,
            required: "80%",
            status: (details.userTokenPercentage || 0) >= 80
          }
        ]
      });
      setShowRequirementsModal(true);
    }
  };

  const handleTestingOverride = () => {
    // Show confirmation for testing mode
    setConfirmationData({
      title: "🧪 Testing Mode: Activate Autopilot",
      message: "TESTING ONLY - All transactions will be prepared but NOT sent!",
      details: [
        "✓ Validate all PublicKeys",
        "✓ Create 10 new trading wallets",
        "⚠️ Prepare SOL distribution (NOT sent)", 
        "⚠️ Prepare USDC distribution (NOT sent)",
        "⚠️ Prepare token distribution (NOT sent)",
        "⚠️ Create test agent (NOT activated)"
      ],
      onConfirm: () => {
        setShowConfirmationModal(false);
        executeAutopilot(true); // Pass testingMode=true
      },
      onCancel: () => setShowConfirmationModal(false)
    });
    setShowConfirmationModal(true);
  };

  // Enable testing mode with keyboard shortcut (Ctrl+Shift+T)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'T') {
        event.preventDefault();
        setShowTestingMode(!showTestingMode);
        console.log(`[TESTING] Testing mode ${!showTestingMode ? 'enabled' : 'disabled'}`);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showTestingMode]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 py-2 xs:py-3 sm:py-4">
        <div className="w-full mx-auto px-2 xs:px-3 sm:px-4 md:px-6" style={{maxWidth: 'min(100%, 1792px)'}}>
          <div className="flex items-center space-x-4">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
          <div className="flex items-center space-x-3">
            <div className="flex items-center">
              {renderTokenImage(tokenData?.token_image || tokenData?.image || '📊')}
              <span style={{ display: 'none' }} className="text-2xl">{tokenData?.token_image || tokenData?.image || '📊'}</span>
            </div>
            <h1 className=" text-sm md:text-xl xl:text-2xl font-bold">{tokenData?.token_name || tokenData?.name || 'Token'} Trading Dashboard</h1>
          </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full mx-auto px-2 xs:px-3 sm:px-4 md:px-6 py-3 xs:py-4 sm:py-6 md:py-8" style={{maxWidth: 'min(100%, 1792px)'}}>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 md:mb-8 break-words">
          Monitor your assets, trading activities, and market making strategies for {tokenData?.symbol || tokenData?.token_symbol || 'this token'}
        </p>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
          {/* Main Wallet Section */}
          <div className="flex flex-col h-full">
            {/* Generated Wallet Balance Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 sm:p-4 md:p-6 flex-1">
            <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-2 mb-4">
              <h3 className="text-xs xs:text-sm font-medium text-gray-600 dark:text-gray-400">Generated Wallet Balance</h3>
              {walletBalanceLoading ? (
                <div className="animate-pulse">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                </div>
              ) : walletBalance?.wallet_address ? (
                <button
                  onClick={() => copyWalletAddress(walletBalance.wallet_address, 'generated')}
                  className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors group"
                  title={copiedWallet === 'generated' ? 'Copied!' : 'Click to copy wallet address'}
                >
                  <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1 xs:px-2 py-0.5 xs:py-1 rounded text-[10px] xs:text-xs">
                    {walletBalance.wallet_address.slice(0, 4)}...{walletBalance.wallet_address.slice(-4)}
                  </span>
                  {copiedWallet === 'generated' ? (
                    <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
                  ) : (
                    <Copy className="w-3 h-3 group-hover:text-blue-500" />
                  )}
                </button>
              ) : null}
            </div>
            {walletBalanceLoading ? (
              <div className="mb-2">
                <div className="flex items-center gap-3 mb-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-500"></div>
                  <span className="text-xl font-bold text-gray-400">Loading wallet balance...</span>
                </div>
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                </div>
              </div>
            ) : (
              <>
                <div className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">
                  ${parseFloat(walletBalance.balance_usd || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </div>
                <p className="text-sm text-gray-500">
                  {walletBalance?.wallet_address ? 'wallet balance' : 'Generated wallet balance'}
                  {walletBalance?.last_updated && (
                    <span className="block text-xs text-gray-400 mt-1">
                      Updated: {new Date(walletBalance.last_updated).toLocaleTimeString()}
                    </span>
                  )}
                </p>
              </>
            )}
            </div>

            {/* Weekly Rewards Card */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/10 dark:to-indigo-900/10 border border-purple-200 dark:border-purple-800/30 rounded-lg shadow-md p-3 sm:p-4 md:p-6 flex-1 mt-3 sm:mt-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg flex items-center justify-center">
                    <span className="text-white text-sm font-bold">🏆</span>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Weekly Rewards</h3>
                    <p className="text-xs text-purple-600 dark:text-purple-400">Generated every Sunday</p>
                  </div>
                </div>
                {rewardWalletLoading ? (
                  <div className="animate-pulse">
                    <div className="h-4 bg-purple-200 dark:bg-purple-700 rounded w-12"></div>
                  </div>
                ) : rewardWalletBalance?.wallet_address && rewardWalletBalance.wallet_address !== 'reward_wallet_placeholder' ? (
                  <button
                    onClick={() => copyWalletAddress(rewardWalletBalance.wallet_address, 'reward')}
                    className="flex items-center space-x-1 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200 transition-colors group"
                    title={copiedWallet === 'reward' ? 'Copied!' : 'Click to copy reward wallet address'}
                  >
                    <span className="font-mono bg-purple-100 dark:bg-purple-900/30 px-2 py-1 rounded">
                      {rewardWalletBalance.wallet_address.slice(0, 4)}...{rewardWalletBalance.wallet_address.slice(-4)}
                    </span>
                    {copiedWallet === 'reward' ? (
                      <Check className="w-3 h-3 text-green-500" />
                    ) : (
                      <Copy className="w-3 h-3 group-hover:text-purple-700 dark:group-hover:text-purple-300" />
                    )}
                  </button>
                ) : (
                  <div className="flex items-center space-x-1 text-xs text-purple-600 dark:text-purple-400">
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                    <span>Active</span>
                  </div>
                )}
              </div>
              
              {rewardWalletLoading ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-purple-500"></div>
                    <span className="text-lg font-bold text-purple-600 dark:text-purple-400">Loading rewards...</span>
                  </div>
                  <div className="animate-pulse space-y-2">
                    <div className="h-3 bg-purple-200 dark:bg-purple-700 rounded w-28"></div>
                    <div className="h-2 bg-purple-200 dark:bg-purple-700 rounded w-20"></div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-gray-900 dark:text-gray-100">
                    ${parseFloat(rewardWalletBalance.balance_usd || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        <span>Performance rewards</span>
                      </span>
                      <span className="text-xs text-gray-500 font-medium">Available</span>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>Next distribution: Sunday</span>
                      {rewardWalletBalance?.last_updated && (
                        <span>
                          Updated: {new Date(rewardWalletBalance.last_updated).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Dynamic Portfolio Chart */}
          <PortfolioChart tokenId={tokenId} assetOverview={assetOverview} assetsLoading={assetsLoading} />
        </div>

        {/* Autopilot Section */}
        <div className="mb-8">
          <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800/30 rounded-2xl p-8 shadow-lg">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {/* Autopilot Info */}
              <div className="flex-1">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                    <Rocket className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                        Autopilot Trading
                      </h3>
                      {showTestingMode && (
                        <div className="flex items-center bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-full px-3 py-1">
                          <span className="text-xs font-bold text-yellow-700 dark:text-yellow-300">🧪 TESTING MODE</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Fully automated market making
                      {showTestingMode && <span className="text-yellow-600 ml-2">(Requirements bypassed)</span>}
                    </p>
                  </div>
                </div>
                
                <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                  Activate autopilot to automatically create 10 trading wallets, distribute funds with variance, 
                  and start intelligent market making. The system will handle everything from setup to execution.
                </p>
                
                {/* Status Indicators */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="flex items-center p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-white/60 dark:border-gray-700/60">
                    <Shield className="w-5 h-5 text-blue-500 mr-3" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Balance Check</p>
                      <p className="font-semibold text-sm">
                        {checkingConditions ? (
                          <span className="text-yellow-600">Checking...</span>
                        ) : autopilotConditions ? (
                          autopilotConditions.conditionsMet ? (
                            <span className="text-green-600">Ready ✓</span>
                          ) : (
                            <span className="text-gray-600">
                              {autopilotConditions.details?.solBalance?.toFixed(2) || 0} SOL
                            </span>
                          )
                        ) : (
                          <span className="text-gray-400">0 SOL</span>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-white/60 dark:border-gray-700/60">
                    <DollarSign className="w-5 h-5 text-green-500 mr-3" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">USDC Balance</p>
                      <p className="font-semibold text-sm">
                        ${(autopilotConditions?.details?.usdcBalance || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-white/60 dark:border-gray-700/60">
                    <Users className="w-5 h-5 text-purple-500 mr-3" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Token Ownership</p>
                      <p className="font-semibold text-sm">
                        {(autopilotConditions?.details?.userTokenPercentage || 0).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Autopilot Controls */}
              <div className="lg:w-80">
                {autopilotStatus?.hasAutopilot ? (
                  /* Active Autopilot Status */
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6 border border-green-200 dark:border-green-800/30 shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-green-500 rounded-full mr-3 animate-pulse"></div>
                        <span className="font-semibold text-green-700 dark:text-green-400">Autopilot Active</span>
                      </div>
                      <Activity className="w-5 h-5 text-green-500" />
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Agent:</span>
                        <span className="font-medium">{autopilotStatus.agents?.[0]?.name}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Wallets:</span>
                        <span className="font-medium">10 Trading Wallets</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Started:</span>
                        <span className="font-medium">
                          {autopilotStatus.agents?.[0]?.createdAt ? 
                            new Date(autopilotStatus.agents[0].createdAt).toLocaleDateString() : 
                            'Today'
                          }
                        </span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => navigate(`/ai-trading/${tokenId}`)}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      View Agent Dashboard
                    </button>
                  </div>
                ) : (
                  /* Activation Button */
                  <div className="text-center">
                    <button
                      onClick={handleAutopilotClick}
                      disabled={checkingConditions || executingAutopilot || autopilotStatus?.hasSuccessfulExecution}
                      className={`w-full py-4 px-6 rounded-xl font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg ${
                        autopilotStatus?.hasSuccessfulExecution
                          ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white cursor-not-allowed'
                          : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 hover:shadow-xl transform hover:scale-105'
                      }`}
                    >
                      {checkingConditions ? (
                        <>
                          <RefreshCw className="w-5 h-5 animate-spin inline mr-2" />
                          Checking Conditions...
                        </>
                      ) : executingAutopilot ? (
                        <>
                          <Zap className="w-5 h-5 animate-pulse inline mr-2" />
                          Activating...
                        </>
                      ) : autopilotStatus?.hasSuccessfulExecution ? (
                        <>
                          <CheckCircle className="w-5 h-5 inline mr-2" />
                          Autopilot Already Activated
                        </>
                      ) : (
                        <>
                          <Rocket className="w-5 h-5 inline mr-2" />
                          Activate Autopilot
                        </>
                      )}
                    </button>

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {autopilotStatus?.hasSuccessfulExecution
                        ? `Activated on ${new Date(autopilotStatus.executedAt).toLocaleDateString()}`
                        : 'Fully automated trading setup & execution'
                      }
                    </p>
                    
                    {/* Testing Override Button */}
                    {showTestingMode && (
                      <button
                        onClick={handleTestingOverride}
                        disabled={executingAutopilot}
                        className="w-full mt-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-3 px-4 rounded-lg font-semibold hover:from-yellow-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 border-2 border-yellow-400"
                      >
                        🧪 Testing Override - Skip Requirements
                      </button>
                    )}
                    
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="space-y-6">
          <div className="flex items-center justify-center mb-2 overflow-x-auto">
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1 shadow-inner min-w-fit">
              {[
                { id: 'guide', label: 'Guide', icon: '📖' },
                { id: 'distribution', label: 'PnL', icon: '💰' },
                { id: 'assets', label: 'Assets', icon: '📊' },
                { id: 'operations', label: 'Ops', icon: '⚡' },
                { id: 'info', label: 'Info', icon: 'ℹ️' }
              ].map(({ id: tab, label, icon }) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`
                    relative px-2 xs:px-3 sm:px-4 py-2 rounded-lg text-xs xs:text-sm font-medium transition-all duration-200 flex-shrink-0
                    ${
                      activeTab === tab
                        ? 'bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-300 shadow-lg transform scale-105'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }
                  `}
                >
                  <span className="flex items-center gap-1 xs:gap-1.5">
                    <span className="text-sm xs:text-base">{icon}</span>
                    <span>{label}</span>
                  </span>
                  {activeTab === tab && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 xs:w-8 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
            <div className="p-3 sm:p-4 md:p-6">
            {/* User Guide Tab */}
            {activeTab === 'guide' && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center text-gray-900 dark:text-gray-100">
                  <Bot className="w-5 h-5 mr-2" />
                  Portal Setup Guide
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">Follow these steps to set up your trading portal for {tokenData?.name || tokenData?.token_name || 'this token'}</p>
                <div className="space-y-4">
                  {[
                    {
                      step: 1,
                      title: 'Fund Main Wallet',
                      description: 'Please ensure your main wallet is funded with a minimum of 0.1 SOL to cover transaction fees and trading operations. This is required for all market making activities.',
                      status: (() => {
                        const solAsset = (assetOverview?.assets || []).find(asset => asset.symbol === 'SOL');
                        const solBalance = parseFloat(solAsset?.balance || 0);
                        return solBalance >= 0.1 ? 'completed' : 'pending';
                      })(),
                      badge: (() => {
                        const solAsset = (assetOverview?.assets || []).find(asset => asset.symbol === 'SOL');
                        const solBalance = parseFloat(solAsset?.balance || 0);
                        return solBalance >= 0.1 ? 'Funded' : 'Fund Wallet';
                      })(),
                      current: (() => {
                        const solAsset = (assetOverview?.assets || []).find(asset => asset.symbol === 'SOL');
                        const solBalance = parseFloat(solAsset?.balance || 0);
                        return `${solBalance.toFixed(3)} SOL`;
                      })()
                    },
                    { step: 2, title: 'Configure Distribution Settings', description: 'Set up the weekly PnL distribution percentages between reward wallet, IP owner, and platform.', status: distributionConfig?.status === 'approved' ? 'approved' : distributionConfig?.status === 'pending' ? 'pending_approval' : 'pending', badge: distributionConfig?.status === 'approved' ? 'Configured' : distributionConfig?.status === 'pending' ? 'Awaiting Approval' : 'Configure Now' },
                    { step: 3, title: 'Create Market Making AI Agent', description: 'Create your market making AI agent in the AI Trading tab to start automated trading.', status: 'pending', badge: 'Go to AI Trading' }
                  ].map(({ step, title, description, status, badge, current }) => (
                    <div key={step} className="flex items-start space-x-4 p-4 border rounded-lg">
                      <div className="w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center font-bold text-sm">
                        {step}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold mb-2">{title}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{description}</p>
                        {status === 'approved' && (
                          <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium  text-green-700 border border-green-200 rounded-md">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {badge}
                          </span>
                        )}
                        {status === 'met' && (
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium  text-green-700 border border-green-200 rounded-md">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {badge}
                            </span>
                            {current && <span className="text-sm text-gray-500">Current: {current}</span>}
                          </div>
                        )}
                        {status === 'completed' && (
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-green-700 border border-green-200 rounded-md">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {badge}
                            </span>
                            {current && <span className="text-sm text-gray-500">Current: {current}</span>}
                          </div>
                        )}
                        {status === 'optional' && (
                          <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium  text-blue-700 border border-blue-200 rounded-md">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {badge}
                          </span>
                        )}
                        {status === 'pending' && (
                          step === 1 ? (
                            <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-md">
                              <Bot className="w-3 h-3 mr-1" />
                              {badge}
                            </span>
                          ) : (
                            <button
                              onClick={step === 2 ? () => setActiveTab('distribution') : step === 3 ? handleGoToAITrading : undefined}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium  text-purple-700 border border-purple-300 rounded-md hover:bg-purple-50 cursor-pointer"
                            >
                              <Bot className="w-3 h-3 mr-1" />
                              {badge}
                            </button>
                          )
                        )}
                        {status === 'pending_approval' && (
                          <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium  text-yellow-700 border border-yellow-200 rounded-md">
                            <Clock className="w-3 h-3 mr-1" />
                            {badge}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Distribution Tab */}
            {activeTab === 'distribution' && (
              <div className="space-y-4 sm:space-y-6">
                {/* Header Section - Mobile Optimized */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                      PnL Distribution
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Configure weekly profit sharing
                    </p>
                  </div>
                  <div className="inline-flex px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-full border border-purple-200 dark:border-purple-800">
                    <span className="text-xs sm:text-sm font-semibold text-purple-700 dark:text-purple-300 whitespace-nowrap">
                      Weekly Settlement
                    </span>
                  </div>
                </div>
                
                {/* Weekly PnL Summary - Responsive Grid */}
                {weeklyPnl && (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10 border border-green-200 dark:border-green-800/30 rounded-xl p-4 sm:p-5 md:p-6">
                    <h4 className="text-sm sm:text-base md:text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">
                      Weekly PnL Report
                    </h4>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
                      <div className="space-y-1">
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Period</p>
                        <p className="text-xs sm:text-sm font-semibold break-words">
                          {weeklyPnl.period ?
                            `${new Date(weeklyPnl.period.start).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})}` :
                            'Current Week'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Distributable</p>
                        <p className="text-sm sm:text-base font-semibold text-green-600">
                          ${(weeklyPnl.total_distributable || 0).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Wallets</p>
                        <p className="text-sm sm:text-base font-semibold">{weeklyPnl.wallets?.length || 0}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Status</p>
                        <p className="text-xs sm:text-sm font-semibold truncate">{weeklyPnl.message || 'Calculating...'}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Current Configuration Status - Mobile First */}
                {distributionConfig?.exists && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 sm:p-5 md:p-6">
                    <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-2 mb-4">
                      <h4 className="text-sm sm:text-base md:text-lg font-semibold">Current Configuration</h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        distributionConfig.config.status === 'approved' ? 'bg-green-100 text-green-800' :
                        distributionConfig.config.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        distributionConfig.config.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {distributionConfig.config.status === 'approved' ? 'Approved' :
                         distributionConfig.config.status === 'pending' ? 'Pending Approval' :
                         distributionConfig.config.status === 'rejected' ? 'Rejected' :
                         distributionConfig.config.status}
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between items-center py-2 border-b dark:border-gray-700">
                        <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Reward Wallet</span>
                        <span className="text-sm sm:text-base font-semibold">{distributionConfig.config.rewardWalletPercentage}%</span>
                      </div>
                      <div className="flex justify-between items-start py-2 border-b dark:border-gray-700">
                        <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          IP Owner
                          <span className="block text-[10px] sm:text-xs truncate max-w-[150px] sm:max-w-none">
                            {distributionConfig.config.ipOwnerEmail}
                          </span>
                        </span>
                        <span className="text-sm sm:text-base font-semibold ml-2">{distributionConfig.config.ipOwnerPercentage}%</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b dark:border-gray-700">
                        <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Platform</span>
                        <span className="text-sm sm:text-base font-semibold">{distributionConfig.config.platformPercentage}%</span>
                      </div>
                    </div>
                    
                    {distributionConfig.config.status === 'pending' && (
                      <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                          <Clock className="inline w-4 h-4 mr-1" />
                          Waiting for approval from:
                        </p>
                        <ul className="text-sm mt-1 ml-5">
                          {!distributionConfig.config.ipOwnerApproved && <li>• IP Owner</li>}
                          {!distributionConfig.config.platformApproved && <li>• Platform</li>}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Configuration Form - Professional Mobile Layout */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 sm:p-5 md:p-6">
                  <h4 className="text-sm sm:text-base md:text-lg font-semibold mb-3 sm:mb-4">
                    {distributionConfig?.exists ? 'Update Distribution' : 'Configure Distribution'}
                  </h4>
                  
                  {/* Important Info - Compact Mobile Design */}
                  <div className="mb-4 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h5 className="text-xs sm:text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center">
                      <Info className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5" />
                      Important Information
                    </h5>
                    <ul className="text-[11px] sm:text-xs md:text-sm text-blue-700 dark:text-blue-300 space-y-1">
                      <li className="flex items-start">
                        <span className="mr-1.5">•</span>
                        <span><strong>Break-even:</strong> 100% realized profit</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-1.5">•</span>
                        <span>Contract details outside platform</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-1.5">•</span>
                        <span>Sunday midnight distribution</span>
                      </li>
                    </ul>
                  </div>

                  {/* Weekly Profit - Mobile Responsive */}
                  {cumulativeProfit !== null && (
                    <div className="mb-4 p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-2">
                        <div className="flex-1">
                          <h5 className="text-xs sm:text-sm font-semibold text-green-800 dark:text-green-200">
                            Weekly Distributable
                          </h5>
                          <p className="text-[10px] sm:text-xs text-green-600 dark:text-green-400 mt-0.5">
                            Current week total
                          </p>
                        </div>
                        <div className="text-left xs:text-right">
                          <p className="text-base sm:text-lg md:text-xl font-bold text-green-800 dark:text-green-200">
                            ${cumulativeProfit.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </p>
                          <p className="text-[10px] sm:text-xs text-green-600 dark:text-green-400 uppercase">
                            USDC
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    
                    // Check if IP owner address is found before submitting
                    if (!distributionFormData.ipOwnerAddress && distributionFormData.ipOwnerEmail) {
                      // Search one more time and show modal if not found
                      await searchIpOwnerByEmail(distributionFormData.ipOwnerEmail, true); // true = show modal
                      return; // Don't proceed with form submission
                    }
                    
                    setDistributionLoading(true);
                    try {
                      const response = await api.post('/distribution/create', {
                        tokenId,
                        ...distributionFormData
                      });
                      
                      if (response.data.success) {
                        setShowSuccessModal(true);
                        // Refresh the configuration
                        const configResponse = await api.get(`/distribution/token/${tokenId}`);
                        setDistributionConfig(configResponse.data);
                      }
                    } catch (error) {
                      console.error('Error submitting distribution config:', error);
                      
                      // Check the type of error from the backend
                      const errorData = error.response?.data;
                      const errorMessage = errorData?.error || 'Failed to submit distribution configuration';

                      if (errorMessage.includes('No IP found') || errorMessage.includes('matching token')) {
                        // Show the professional modal instead of alert
                        setNoIpModalData({
                          email: distributionFormData.ipOwnerEmail,
                          token: tokenData?.name || tokenData?.ticker || tokenId
                        });
                        setShowNoIpModal(true);
                      } else if (errorData?.status === 'locked') {
                        // Handle locked configuration (approved or partially approved)
                        let lockMessage = errorMessage;
                        if (errorData.currentStatus === 'approved') {
                          lockMessage += '\n\nOnce a distribution configuration is approved by both parties, it cannot be modified to ensure agreement integrity.';
                        } else if (errorData.currentStatus === 'partial_approved') {
                          const approvalStatus = [];
                          if (errorData.ipOwnerApproved) approvalStatus.push('IP Owner');
                          if (errorData.platformApproved) approvalStatus.push('Platform');
                          lockMessage += `\n\nCurrently approved by: ${approvalStatus.join(', ')}`;
                        }
                        setErrorMessage(lockMessage);
                        setShowErrorModal(true);
                      } else {
                        // Show professional error modal
                        setErrorMessage(errorMessage);
                        setShowErrorModal(true);
                      }
                    } finally {
                      setDistributionLoading(false);
                    }
                  }}>
                    
                    {/* IP Owner Email */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                        IP Owner Email
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          required
                          value={distributionFormData.ipOwnerEmail}
                          onChange={(e) => {
                            const email = e.target.value;
                            setDistributionFormData({
                              ...distributionFormData,
                              ipOwnerEmail: email,
                              // Clear previous IP owner data when email changes
                              ipOwnerAddress: '',
                              ipOwnerName: ''
                            });
                            // Auto-search when email is complete (but don't show modal)
                            if (email.includes('@') && email.includes('.')) {
                              searchIpOwnerByEmail(email, false); // false = don't show modal
                            }
                          }}
                          onBlur={() => {
                            if (distributionFormData.ipOwnerEmail) {
                              searchIpOwnerByEmail(distributionFormData.ipOwnerEmail, false); // false = don't show modal
                            }
                          }}
                          className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:border-gray-600 transition-all duration-200"
                          placeholder="Enter IP owner's email address"
                        />
                        {searchingIpOwner && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                          </div>
                        )}
                      </div>
                      
                      {/* Show auto-populated address */}
                      {distributionFormData.ipOwnerAddress && (
                        <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                          <div className="flex items-start space-x-2">
                            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                                Wallet Found {distributionFormData.ipOwnerName && `for ${distributionFormData.ipOwnerName}`}
                              </p>
                              <p className="text-xs text-green-600 dark:text-green-400 font-mono mt-1">
                                {distributionFormData.ipOwnerAddress}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {!distributionFormData.ipOwnerAddress && distributionFormData.ipOwnerEmail && !searchingIpOwner && (
                        <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                          <div className="flex items-start space-x-2">
                            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                No IP found for this email
                              </p>
                              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                                Please verify the email address or ensure the IP exists on Giggle platform
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {!distributionFormData.ipOwnerEmail && (
                        <p className="text-xs text-gray-500 mt-2">
                          Wallet address will be automatically populated when email is entered
                        </p>
                      )}
                      
                    </div>
                    
                    {/* Distribution Percentages - Mobile Optimized */}
                    <div className="mb-6 space-y-4 sm:space-y-6">

                      {/* Pre-Breakeven Distribution - Mobile Optimized */}
                      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-5 md:p-6 bg-gray-50 dark:bg-gray-800/50">
                        <h5 className="text-sm sm:text-base md:text-lg font-semibold mb-3 sm:mb-4 text-gray-800 dark:text-gray-200">
                          Pre-Breakeven <span className="text-xs sm:text-sm font-normal text-gray-500">({70}/{27}/{3}%)</span>
                        </h5>
                        <div className="grid gap-3 sm:gap-4 md:gap-6 grid-cols-1 xs:grid-cols-3">
                          {/* Profit Receiver Wallet - Compact Mobile */}
                          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-purple-200 dark:border-purple-800">
                            <div className="flex items-center mb-2 sm:mb-3">
                              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-600 rounded-md sm:rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                                <span className="text-white text-xs sm:text-sm font-bold">💰</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <label className="block text-xs sm:text-sm font-medium text-purple-800 dark:text-purple-200 truncate">
                                  Profit Wallet
                                </label>
                                <p className="text-[10px] sm:text-xs text-purple-600 dark:text-purple-400 truncate">
                                  {rewardWalletBalance?.wallet_address ?
                                    `${rewardWalletBalance.wallet_address.slice(0, 4)}...${rewardWalletBalance.wallet_address.slice(-4)}` :
                                    'Your wallet'}
                                </p>
                              </div>
                            </div>
                            <div className="relative">
                              <input
                                type="text"
                                inputMode="decimal"
                                required
                                value={distributionFormData.preBreakevenRewardPercentage === 0 ? '' : distributionFormData.preBreakevenRewardPercentage}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (inputValue === '' || inputValue === '.') {
                                    setDistributionFormData({
                                      ...distributionFormData,
                                      preBreakevenRewardPercentage: 0
                                    });
                                  } else {
                                    const numericValue = parseFloat(inputValue);
                                    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
                                      setDistributionFormData({
                                        ...distributionFormData,
                                        preBreakevenRewardPercentage: numericValue
                                      });
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  setDistributionFormData({
                                    ...distributionFormData,
                                    preBreakevenRewardPercentage: Math.min(100, Math.max(0, value))
                                  });
                                }}
                                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 pr-6 sm:pr-8 border-2 border-purple-200 dark:border-purple-700 rounded-md sm:rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 text-center font-semibold text-sm sm:text-base"
                                placeholder="70"
                              />
                              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-purple-600 dark:text-purple-400 font-medium text-xs sm:text-sm">%</span>
                            </div>
                          </div>
                          
                          {/* IP Owner */}
                          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center mb-3">
                              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                                <span className="text-white text-sm font-bold">👑</span>
                              </div>
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-blue-800 dark:text-blue-200">
                                  IP Owner
                                </label>
                                <p className="text-xs text-blue-600 dark:text-blue-400">
                                  {distributionFormData.ipOwnerEmail || 'IP owner email not set'}
                                </p>
                              </div>
                            </div>
                            <div className="relative">
                              <input
                                type="text"
                                inputMode="decimal"
                                required
                                value={distributionFormData.preBreakevenIpOwnerPercentage === 0 ? '' : distributionFormData.preBreakevenIpOwnerPercentage}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (inputValue === '' || inputValue === '.') {
                                    setDistributionFormData({
                                      ...distributionFormData,
                                      preBreakevenIpOwnerPercentage: 0
                                    });
                                  } else {
                                    const numericValue = parseFloat(inputValue);
                                    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
                                      setDistributionFormData({
                                        ...distributionFormData,
                                        preBreakevenIpOwnerPercentage: numericValue
                                      });
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  setDistributionFormData({
                                    ...distributionFormData,
                                    preBreakevenIpOwnerPercentage: Math.min(100, Math.max(0, value))
                                  });
                                }}
                                className="w-full px-3 py-2 pr-8 border-2 border-blue-200 dark:border-blue-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 text-center font-semibold"
                                placeholder="27"
                              />
                              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-600 dark:text-blue-400 font-medium">%</span>
                            </div>
                          </div>
                          
                          {/* Platform */}
                          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 rounded-xl border border-green-200 dark:border-green-800">
                            <div className="flex items-center mb-3">
                              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center mr-3">
                                <span className="text-white text-sm font-bold">🏢</span>
                              </div>
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-green-800 dark:text-green-200">
                                  Platform
                                </label>
                                <p className="text-xs text-green-600 dark:text-green-400">
                                  platform@giggle.com
                                </p>
                              </div>
                            </div>
                            <div className="relative">
                              <input
                                type="text"
                                inputMode="decimal"
                                required
                                value={distributionFormData.preBreakevenPlatformPercentage === 0 ? '' : distributionFormData.preBreakevenPlatformPercentage}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (inputValue === '' || inputValue === '.') {
                                    setDistributionFormData({
                                      ...distributionFormData,
                                      preBreakevenPlatformPercentage: 0
                                    });
                                  } else {
                                    const numericValue = parseFloat(inputValue);
                                    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
                                      setDistributionFormData({
                                        ...distributionFormData,
                                        preBreakevenPlatformPercentage: numericValue
                                      });
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  setDistributionFormData({
                                    ...distributionFormData,
                                    preBreakevenPlatformPercentage: Math.min(100, Math.max(0, value))
                                  });
                                }}
                                className="w-full px-3 py-2 pr-8 border-2 border-green-200 dark:border-green-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 text-center font-semibold"
                                placeholder="3"
                              />
                              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-600 dark:text-green-400 font-medium">%</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Pre-Breakeven Total Validation */}
                        <div className={`p-3 rounded-lg mt-4 ${
                          Math.abs((distributionFormData.preBreakevenRewardPercentage + 
                                   distributionFormData.preBreakevenIpOwnerPercentage + 
                                   distributionFormData.preBreakevenPlatformPercentage) - 100) < 0.01
                            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                        }`}>
                          <p className="text-sm font-medium">
                            Pre-Breakeven Total: {(distributionFormData.preBreakevenRewardPercentage + 
                                    distributionFormData.preBreakevenIpOwnerPercentage + 
                                    distributionFormData.preBreakevenPlatformPercentage).toFixed(2)}%
                            {Math.abs((distributionFormData.preBreakevenRewardPercentage + 
                                      distributionFormData.preBreakevenIpOwnerPercentage + 
                                      distributionFormData.preBreakevenPlatformPercentage) - 100) < 0.01
                              ? ' ✓'
                              : ' (Must equal 100%)'}
                          </p>
                        </div>
                      </div>

                      {/* Post-Breakeven Distribution - Mobile Optimized */}
                      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-5 md:p-6 bg-gray-50 dark:bg-gray-800/50">
                        <h5 className="text-sm sm:text-base md:text-lg font-semibold mb-3 sm:mb-4 text-gray-800 dark:text-gray-200">
                          Post-Breakeven <span className="text-xs sm:text-sm font-normal text-gray-500">({30}/{63}/{7}%)</span>
                        </h5>
                        <div className="grid gap-3 sm:gap-4 md:gap-6 grid-cols-1 xs:grid-cols-3">
                          {/* Profit Receiver Wallet - Compact Mobile */}
                          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-purple-200 dark:border-purple-800">
                            <div className="flex items-center mb-2 sm:mb-3">
                              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-600 rounded-md sm:rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                                <span className="text-white text-xs sm:text-sm font-bold">💰</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <label className="block text-xs sm:text-sm font-medium text-purple-800 dark:text-purple-200 truncate">
                                  Profit Wallet
                                </label>
                                <p className="text-[10px] sm:text-xs text-purple-600 dark:text-purple-400 truncate">
                                  {rewardWalletBalance?.wallet_address ?
                                    `${rewardWalletBalance.wallet_address.slice(0, 4)}...${rewardWalletBalance.wallet_address.slice(-4)}` :
                                    'Your wallet'}
                                </p>
                              </div>
                            </div>
                            <div className="relative">
                              <input
                                type="text"
                                inputMode="decimal"
                                required
                                value={distributionFormData.postBreakevenRewardPercentage === 0 ? '' : distributionFormData.postBreakevenRewardPercentage}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (inputValue === '' || inputValue === '.') {
                                    setDistributionFormData({
                                      ...distributionFormData,
                                      postBreakevenRewardPercentage: 0
                                    });
                                  } else {
                                    const numericValue = parseFloat(inputValue);
                                    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
                                      setDistributionFormData({
                                        ...distributionFormData,
                                        postBreakevenRewardPercentage: numericValue
                                      });
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  setDistributionFormData({
                                    ...distributionFormData,
                                    postBreakevenRewardPercentage: Math.min(100, Math.max(0, value))
                                  });
                                }}
                                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 pr-6 sm:pr-8 border-2 border-purple-200 dark:border-purple-700 rounded-md sm:rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 text-center font-semibold text-sm sm:text-base"
                                placeholder="30"
                              />
                              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-purple-600 dark:text-purple-400 font-medium text-xs sm:text-sm">%</span>
                            </div>
                          </div>

                          {/* IP Owner - Compact Mobile */}
                          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center mb-2 sm:mb-3">
                              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-600 rounded-md sm:rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                                <span className="text-white text-xs sm:text-sm font-bold">👑</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <label className="block text-xs sm:text-sm font-medium text-blue-800 dark:text-blue-200 truncate">
                                  IP Owner
                                </label>
                                <p className="text-[10px] sm:text-xs text-blue-600 dark:text-blue-400 truncate">
                                  {distributionFormData.ipOwnerEmail || 'Email not set'}
                                </p>
                              </div>
                            </div>
                            <div className="relative">
                              <input
                                type="text"
                                inputMode="decimal"
                                required
                                value={distributionFormData.postBreakevenIpOwnerPercentage === 0 ? '' : distributionFormData.postBreakevenIpOwnerPercentage}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (inputValue === '' || inputValue === '.') {
                                    setDistributionFormData({
                                      ...distributionFormData,
                                      postBreakevenIpOwnerPercentage: 0
                                    });
                                  } else {
                                    const numericValue = parseFloat(inputValue);
                                    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
                                      setDistributionFormData({
                                        ...distributionFormData,
                                        postBreakevenIpOwnerPercentage: numericValue
                                      });
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  setDistributionFormData({
                                    ...distributionFormData,
                                    postBreakevenIpOwnerPercentage: Math.min(100, Math.max(0, value))
                                  });
                                }}
                                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 pr-6 sm:pr-8 border-2 border-blue-200 dark:border-blue-700 rounded-md sm:rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 text-center font-semibold text-sm sm:text-base"
                                placeholder="63"
                              />
                              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-blue-600 dark:text-blue-400 font-medium text-xs sm:text-sm">%</span>
                            </div>
                          </div>

                          {/* Platform - Compact Mobile */}
                          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-green-200 dark:border-green-800">
                            <div className="flex items-center mb-2 sm:mb-3">
                              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-600 rounded-md sm:rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                                <span className="text-white text-xs sm:text-sm font-bold">🏢</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <label className="block text-xs sm:text-sm font-medium text-green-800 dark:text-green-200 truncate">
                                  Platform
                                </label>
                                <p className="text-[10px] sm:text-xs text-green-600 dark:text-green-400 truncate">
                                  platform@giggle.com
                                </p>
                              </div>
                            </div>
                            <div className="relative">
                              <input
                                type="text"
                                inputMode="decimal"
                                required
                                value={distributionFormData.postBreakevenPlatformPercentage === 0 ? '' : distributionFormData.postBreakevenPlatformPercentage}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (inputValue === '' || inputValue === '.') {
                                    setDistributionFormData({
                                      ...distributionFormData,
                                      postBreakevenPlatformPercentage: 0
                                    });
                                  } else {
                                    const numericValue = parseFloat(inputValue);
                                    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
                                      setDistributionFormData({
                                        ...distributionFormData,
                                        postBreakevenPlatformPercentage: numericValue
                                      });
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  setDistributionFormData({
                                    ...distributionFormData,
                                    postBreakevenPlatformPercentage: Math.min(100, Math.max(0, value))
                                  });
                                }}
                                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 pr-6 sm:pr-8 border-2 border-green-200 dark:border-green-700 rounded-md sm:rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 text-center font-semibold text-sm sm:text-base"
                                placeholder="7"
                              />
                              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-green-600 dark:text-green-400 font-medium text-xs sm:text-sm">%</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Post-Breakeven Total Validation - Mobile Responsive */}
                        <div className={`p-2 sm:p-3 rounded-lg mt-3 sm:mt-4 ${
                          Math.abs((distributionFormData.postBreakevenRewardPercentage +
                                   distributionFormData.postBreakevenIpOwnerPercentage +
                                   distributionFormData.postBreakevenPlatformPercentage) - 100) < 0.01
                            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                        }`}>
                          <p className="text-xs sm:text-sm font-medium">
                            Post-Breakeven Total: {(distributionFormData.postBreakevenRewardPercentage +
                                    distributionFormData.postBreakevenIpOwnerPercentage +
                                    distributionFormData.postBreakevenPlatformPercentage).toFixed(2)}%
                            {Math.abs((distributionFormData.postBreakevenRewardPercentage +
                                      distributionFormData.postBreakevenIpOwnerPercentage +
                                      distributionFormData.postBreakevenPlatformPercentage) - 100) < 0.01
                              ? ' ✓'
                              : ' (Must equal 100%)'}
                          </p>
                        </div>
                      </div>
                      
                    </div>
                    
                    <button
                      type="submit"
                      disabled={distributionLoading ||
                               distributionConfig?.config?.status === 'approved' ||
                               distributionConfig?.config?.status === 'partial_approved' ||
                               Math.abs((distributionFormData.preBreakevenRewardPercentage +
                                        distributionFormData.preBreakevenIpOwnerPercentage +
                                        distributionFormData.preBreakevenPlatformPercentage) - 100) >= 0.01 ||
                               Math.abs((distributionFormData.postBreakevenRewardPercentage +
                                        distributionFormData.postBreakevenIpOwnerPercentage +
                                        distributionFormData.postBreakevenPlatformPercentage) - 100) >= 0.01}
                      className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {distributionLoading ? 'Submitting...' :
                       distributionConfig?.config?.status === 'approved' ? 'Configuration Approved & Locked' :
                       distributionConfig?.config?.status === 'partial_approved' ? 'Awaiting Final Approval' :
                       distributionConfig?.exists ? 'Update Configuration' : 'Submit Configuration'}
                    </button>
                  </form>
                  
                  <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      <Info className="inline w-4 h-4 mr-1" />
                      After submission, both the IP owner and platform will receive approval emails.
                      The configuration will be active once both parties approve.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* No IP Found Modal */}
            {showNoIpModal && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-gray-200 dark:border-gray-700">
                  {/* Modal Header */}
                  <div className="bg-gradient-to-r from-red-500 to-pink-500 px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">IP Not Found</h3>
                        <p className="text-red-100 text-sm">Email verification failed</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Modal Body */}
                  <div className="p-3 sm:p-4 md:p-6">
                    <div className="mb-4">
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        The email{' '}
                        <span className="font-semibold text-red-600 dark:text-red-400">
                          {noIpModalData.email}
                        </span>{' '}
                        may have intellectual properties, but none match the current token{' '}
                        <span className="font-semibold text-purple-600 dark:text-purple-400">
                          {noIpModalData.token}
                        </span>.
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center">
                        <Info className="w-4 h-4 mr-2 text-blue-500" />
                        Possible Solutions
                      </h4>
                      <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                        <li className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          Verify this email owns the <span className="font-medium text-purple-600 dark:text-purple-400">{noIpModalData.token}</span> token
                        </li>
                        <li className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          Check if the token is properly launched on Giggle
                        </li>
                        <li className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          Contact the IP owner to ensure token ownership
                        </li>
                        <li className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          Try a different email address if this isn't the owner
                        </li>
                      </ul>
                    </div>
                    
                    
                    {/* Modal Actions */}
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowNoIpModal(false)}
                        className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        Try Again
                      </button>
                      <button
                        onClick={() => {
                          setShowNoIpModal(false);
                          setDistributionFormData(prev => ({ ...prev, ipOwnerEmail: '' }));
                        }}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg font-medium hover:from-red-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105"
                      >
                        Clear Email
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-gray-200 dark:border-gray-700">
                  {/* Modal Header */}
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">Configuration Submitted!</h3>
                        <p className="text-green-100 text-sm">Approval process started</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Modal Body */}
                  <div className="p-3 sm:p-4 md:p-6">
                    <div className="mb-6">
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                        Your distribution configuration has been submitted successfully! 
                        Approval emails have been sent to both parties.
                      </p>
                      
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-4">
                        <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center">
                          <Clock className="w-4 h-4 mr-2 text-blue-500" />
                          What Happens Next?
                        </h4>
                        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-2">
                          <li className="flex items-start">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                            IP Owner receives approval email with distribution details
                          </li>
                          <li className="flex items-start">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                            Platform team reviews and approves the configuration
                          </li>
                          <li className="flex items-start">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                            Weekly PnL distributions begin once both parties approve
                          </li>
                        </ul>
                      </div>
                      
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                        <p className="text-sm text-green-700 dark:text-green-300">
                          <CheckCircle className="inline w-4 h-4 mr-1" />
                          You can track the approval status in the Distribution tab above. 
                          You'll be notified when the configuration is fully approved.
                        </p>
                      </div>
                    </div>
                    
                    {/* Modal Actions */}
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowSuccessModal(false)}
                        className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-medium hover:from-green-600 hover:to-emerald-600 transition-all duration-200 transform hover:scale-105"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Asset Overview Tab */}
            {activeTab === 'assets' && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Asset Breakdown</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Current holdings across {assetOverview?.walletCount || 0} wallet{assetOverview?.walletCount !== 1 ? 's' : ''}
                </p>
                {assetsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">Loading real-time blockchain data...</p>
                  </div>
                ) : (assetOverview?.assets || []).length === 0 ? (
                  <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                    <p>No assets found in wallets for this token scope.</p>
                    <p className="text-sm mt-2">Add wallets to this token to see balances.</p>
                  </div>
                ) : (
                  <>
                  {(assetOverview?.assets || []).map((asset, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                        {asset.symbol.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{asset.name}</span>
                          {asset.mint && (
                            <div className="relative group">
                              <button
                                onClick={() => copyMintAddress(asset.mint, asset.symbol)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                title={asset.mint}
                              >
                                {copiedMint === asset.symbol ? (
                                  <Check className="w-4 h-4 text-green-500" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </button>
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                                {copiedMint === asset.symbol ? 'Copied!' : `Click to copy: ${asset.mint.slice(0, 4)}...${asset.mint.slice(-4)}`}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{asset.symbol}</div>
                      </div>
                    </div>
                    <div className="text-center flex-1">
                      <div className="font-semibold">
                        {(parseFloat(asset.balance) || 0).toLocaleString(undefined, {
                          minimumFractionDigits: asset.symbol === 'SOL' ? 3 : (asset.balance < 1 ? 2 : 0),
                          maximumFractionDigits: asset.symbol === 'SOL' ? 3 : (asset.balance < 1 ? 6 : 2)
                        })} {asset.symbol}
                      </div>
                      <div className="text-sm text-gray-500">
                        ${(parseFloat(asset.value) || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </div>
                    </div>
                    <div className="text-right flex-1">
                      <div className="font-semibold">
                        {formatPrice(parseFloat(asset.price) || 0)}
                      </div>
                      <div className={`text-sm flex items-center justify-end ${
                        !asset.change24h || asset.change24h === 0 
                          ? 'text-gray-500' 
                          : asset.change24h > 0 
                            ? 'text-green-600' 
                            : 'text-red-600'
                      }`}>
                        {!asset.change24h || asset.change24h === 0 ? (
                          <span>0.00%</span>
                        ) : asset.change24h > 0 ? (
                          <>
                            <ArrowUpRight className="w-3 h-3 mr-1" />
                            +{Math.abs(asset.change24h).toFixed(2)}%
                          </>
                        ) : (
                          <>
                            <ArrowDownRight className="w-3 h-3 mr-1" />
                            -{Math.abs(asset.change24h).toFixed(2)}%
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  ))}
                  
                  {/* Total Row */}
                  {(assetOverview?.assets || []).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          Total Portfolio Value
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          ${(parseFloat(assetOverview?.totalValueUsd) || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                  </>
                )}
              </div>
            )}

            {/* Operation Log Tab */}
            {activeTab === 'operations' && (
              <div className="space-y-4 sm:space-y-6">
                {/* Header with title and summary - Mobile Optimized */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div>
                    <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100">Operation History</h3>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                      {operationsSubTab === 'trades'
                        ? `Buy and sell transactions for ${tokenData?.symbol || 'Token'}`
                        : `AI commands and fund transfers for ${tokenData?.symbol || 'Token'}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      {totalOperations} {operationsSubTab === 'trades' ? 'Trades' : 'Commands'}
                    </span>
                  </div>
                </div>

                {/* Sub-tabs for Operations - Mobile Responsive */}
                <div className="flex space-x-1 bg-gray-100 dark:bg-gray-900 p-1 rounded-lg w-full sm:w-fit overflow-x-auto">
                  <button
                    onClick={() => {
                      setOperationsSubTab('commands');
                      setCurrentPage(1);
                      // Reset type filter if it's set to trade-specific values
                      if (typeFilter === 'buy' || typeFilter === 'sell') {
                        setTypeFilter('all');
                      }
                    }}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap flex-shrink-0 ${
                      operationsSubTab === 'commands'
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    <span className="hidden sm:inline">AI Commands & Transfers</span>
                    <span className="sm:hidden">Commands</span>
                  </button>
                  <button
                    onClick={() => {
                      setOperationsSubTab('trades');
                      setCurrentPage(1);
                      // Reset type filter if it's set to non-trade values
                      if (typeFilter === 'AI Command' || typeFilter === 'Transfer') {
                        setTypeFilter('all');
                      }
                    }}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap flex-shrink-0 ${
                      operationsSubTab === 'trades'
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                    <span className="hidden sm:inline">Trades Only</span>
                    <span className="sm:hidden">Trades</span>
                  </button>
                </div>

                {/* Search and Filter Controls - Mobile Optimized */}
                <div className="bg-gray-50 dark:bg-gray-900 border dark:border-gray-700 rounded-xl p-3 sm:p-4 space-y-3 sm:space-y-4">
                  {/* Search Bar - Mobile First */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <input
                        type="text"
                        placeholder={`Search ${operationsSubTab === 'trades' ? 'trades' : 'operations'}...`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-8 sm:pl-10 pr-8 sm:pr-10 py-1.5 sm:py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm('')}
                          className="absolute right-2.5 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                      )}
                    </div>

                    {/* Filter Toggle - Mobile Friendly */}
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 border rounded-lg transition-colors text-xs sm:text-sm whitespace-nowrap ${
                        showFilters
                          ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-300'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden xs:inline">Filters</span>
                      {(typeFilter !== 'all' || statusFilter !== 'all' || dateFilter !== 'all') && (
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-500 rounded-full"></div>
                      )}
                    </button>
                  </div>

                  {/* Advanced Filters - Mobile Responsive Grid */}
                  {showFilters && (
                    <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                      {/* Type Filter */}
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {operationsSubTab === 'trades' ? 'Trade Type' : 'Type'}
                        </label>
                        <select
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                          className="w-full text-xs sm:text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg py-1.5 sm:py-2 px-2 sm:px-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          {operationsSubTab === 'trades' ? (
                            // Trade-specific filters
                            <>
                              <option value="all">All Trades</option>
                              <option value="buy">Buy Orders</option>
                              <option value="sell">Sell Orders</option>
                            </>
                          ) : operationsSubTab === 'commands' ? (
                            // Command & Transfer filters (no Trade option)
                            <>
                              <option value="all">All Types</option>
                              <option value="AI Command">AI Commands</option>
                              <option value="Transfer">Transfers</option>
                            </>
                          ) : (
                            // General operation filters (fallback, shouldn't happen)
                            <>
                              <option value="all">All Types</option>
                              <option value="AI Command">AI Commands</option>
                              <option value="Trade">Trades</option>
                              <option value="Transfer">Transfers</option>
                            </>
                          )}
                        </select>
                      </div>

                      {/* Status Filter */}
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="w-full text-xs sm:text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg py-1.5 sm:py-2 px-2 sm:px-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="completed">Completed</option>
                          <option value="failed">Failed</option>
                        </select>
                      </div>

                      {/* Date Filter */}
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Time Period</label>
                        <select
                          value={dateFilter}
                          onChange={(e) => setDateFilter(e.target.value)}
                          className="w-full text-xs sm:text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg py-1.5 sm:py-2 px-2 sm:px-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="all">All Time</option>
                          <option value="today">Today</option>
                          <option value="week">Last Week</option>
                          <option value="month">Last Month</option>
                          <option value="quarter">Last Quarter</option>
                          <option value="custom">Custom Range</option>
                        </select>
                      </div>

                      {/* Items per page */}
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Show</label>
                        <select
                          value={itemsPerPage}
                          onChange={(e) => setItemsPerPage(Number(e.target.value))}
                          className="w-full text-xs sm:text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg py-1.5 sm:py-2 px-2 sm:px-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="10">10 per page</option>
                          <option value="25">25 per page</option>
                          <option value="50">50 per page</option>
                          <option value="100">100 per page</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Custom Date Range - Mobile Responsive */}
                  {showFilters && dateFilter === 'custom' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From Date</label>
                        <input
                          type="date"
                          value={customDateFrom}
                          onChange={(e) => setCustomDateFrom(e.target.value)}
                          className="w-full text-xs sm:text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg py-1.5 sm:py-2 px-2 sm:px-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To Date</label>
                        <input
                          type="date"
                          value={customDateTo}
                          onChange={(e) => setCustomDateTo(e.target.value)}
                          className="w-full text-xs sm:text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg py-1.5 sm:py-2 px-2 sm:px-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                    </div>
                  )}

                  {/* Clear Filters - Mobile Responsive */}
                  {(searchTerm || typeFilter !== 'all' || statusFilter !== 'all' || dateFilter !== 'all') && (
                    <div className="pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={clearFilters}
                        className="text-xs sm:text-sm text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 font-medium"
                      >
                        Clear All Filters
                      </button>
                    </div>
                  )}
                </div>

                {/* Operations List - Mobile Optimized */}
                {operationsLoading ? (
                  <div className="text-center py-8 sm:py-12">
                    <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-3 sm:mt-4">Loading operations...</p>
                  </div>
                ) : operations.length === 0 ? (
                  <div className="text-center py-8 sm:py-12 bg-gray-50 dark:bg-gray-900 rounded-xl">
                    <div className="text-gray-400 mb-3 sm:mb-4">
                      <Clock className="w-8 h-8 sm:w-12 sm:h-12 mx-auto" />
                    </div>
                    <p className="text-sm sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No operations found</p>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 px-4">
                      {searchTerm || typeFilter !== 'all' || statusFilter !== 'all' || dateFilter !== 'all'
                        ? 'Try adjusting your filters to see more results.'
                        : 'Operations will appear here when you use AI commands, make trades, or transfer funds.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {operations.map((operation) => {
                      const isTradeView = operationsSubTab === 'trades';
                      const metadata = operation.metadata || {};
                      const tradeType = metadata.type; // 'buy' or 'sell'
                      
                      return (
                        <div key={operation.id} className={`p-3 sm:p-4 border dark:border-gray-700 rounded-lg sm:rounded-xl ${
                          isTradeView ? 'bg-gradient-to-r from-gray-50 to-white dark:from-gray-900 dark:to-gray-800' : 'bg-white dark:bg-gray-800'
                        }`}>
                          <div className="flex items-start sm:items-center justify-between gap-3">
                            <div className="flex items-start sm:items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
                              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isTradeView && tradeType === 'buy' ? 'bg-green-100 text-green-600' :
                                isTradeView && tradeType === 'sell' ? 'bg-red-100 text-red-600' :
                                'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                              }`}>
                                {(operation.type === 'AI Command' || operation.operation_type === 'ai_command') && <Bot className="w-4 h-4 sm:w-5 sm:h-5" />}
                                {(operation.type === 'Trade' || operation.operation_type === 'trade') && (
                                  isTradeView && tradeType === 'buy' ? <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5" /> :
                                  isTradeView && tradeType === 'sell' ? <ArrowDownRight className="w-4 h-4 sm:w-5 sm:h-5" /> :
                                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />
                                )}
                                {(operation.type === 'Transfer' || operation.operation_type === 'transfer') && <Send className="w-4 h-4 sm:w-5 sm:h-5" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start gap-2">
                                  <div className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-100 truncate flex-1">
                                    {isTradeView && tradeType ?
                                      `${tradeType.charAt(0).toUpperCase() + tradeType.slice(1)} Order` :
                                      operation.type || operation.operation_type
                                    }
                                  </div>
                                  {isTradeView && metadata.dexUsed && (
                                    <span className="inline-flex px-1.5 py-0.5 text-[10px] sm:text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md whitespace-nowrap flex-shrink-0">
                                      {metadata.dexUsed}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{operation.description}</div>
                                {isTradeView && metadata.walletAddress && (
                                  <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1 truncate">
                                    Wallet: {metadata.walletAddress.slice(0, 6)}...{metadata.walletAddress.slice(-4)}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className={`px-1.5 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-md whitespace-nowrap ${
                                operation.status === 'Active' || operation.status === 'active'
                                  ? 'bg-black dark:bg-white text-white dark:text-black'
                                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600'
                              }`}>
                                {operation.status}
                              </span>
                              <div className="text-[10px] sm:text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center justify-end">
                                <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-1" />
                                <span className="truncate">
                                  {operation.timestamp ||
                                   (operation.created_at ? new Date(operation.created_at).toLocaleString('en-US', {
                                     month: '2-digit',
                                     day: '2-digit',
                                     hour: '2-digit',
                                     minute: '2-digit',
                                     hour12: false
                                   }) : 'No timestamp')
                                  }
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pagination - Mobile Responsive */}
                {!operationsLoading && operations.length > 0 && totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 order-2 sm:order-1">
                      <span className="hidden sm:inline">Showing </span>
                      {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalOperations)} of {totalOperations}
                    </div>

                    <div className="flex items-center gap-0.5 sm:gap-1 order-1 sm:order-2">
                      {/* Previous Button */}
                      <button
                        onClick={goToPrevPage}
                        disabled={currentPage === 1}
                        className={`p-1.5 sm:p-2 rounded-lg border transition-colors ${
                          currentPage === 1
                            ? 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>

                      {/* Page Numbers - Mobile Optimized */}
                      {generatePageNumbers().map((page, index) => (
                        page === '...' ? (
                          <span key={index} className="px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm text-gray-400 dark:text-gray-500">...</span>
                        ) : (
                          <button
                            key={index}
                            onClick={() => goToPage(page)}
                            className={`px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm rounded-lg border transition-colors ${
                              currentPage === page
                                ? 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 font-medium'
                                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      ))}

                      {/* Next Button */}
                      <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`p-1.5 sm:p-2 rounded-lg border transition-colors ${
                          currentPage === totalPages
                            ? 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Token Info Tab */}
            {activeTab === 'info' && (
              <div className="space-y-4 sm:space-y-6">
                {/* Header Section - Mobile Optimized */}
                <div className="flex flex-col gap-2">
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold flex items-center text-gray-900 dark:text-gray-100">
                    <Info className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                    <span>Token Information</span>
                    {tokenData?.market_data?.last_updated && (
                      <span className="ml-2 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
                        Updated: {new Date(tokenData.market_data.last_updated).toLocaleTimeString()}
                      </span>
                    )}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    {tokenData?.market_data ? 'Live market data' : 'Static market data'} for {tokenData?.token_name || tokenData?.name || 'Token'}
                  </p>
                  {/* Mobile Update Time */}
                  {tokenData?.market_data?.last_updated && (
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 sm:hidden">
                      Updated: {new Date(tokenData.market_data.last_updated).toLocaleTimeString()}
                    </p>
                  )}
                </div>
                
                {/* Mint Address - Mobile Responsive */}
                {(tokenData?.mint_address || tokenData?.token_id) && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-3 sm:p-4">
                    <div className="text-xs sm:text-sm text-gray-500 mb-1">Mint Address</div>
                    <div className="font-mono text-xs sm:text-sm break-all text-gray-800 dark:text-gray-200 leading-relaxed">
                      {tokenData.mint_address || tokenData.token_id}
                    </div>
                  </div>
                )}
                
                {tokenData?.market_data ? (
                  // Real market data - Mobile Responsive Grid
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                    <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">Market Cap</div>
                      <div className="text-sm sm:text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
                        ${tokenData.market_data.market_cap_usd ? (parseFloat(tokenData.market_data.market_cap_usd) / 1000000).toFixed(1) + 'M' : 'N/A'}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">Current Price</div>
                      <div className="text-sm sm:text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 break-all">
                        ${tokenData.market_data.price_usd ? parseFloat(tokenData.market_data.price_usd).toFixed(8) : 'N/A'}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700 col-span-2 sm:col-span-1">
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">24h Volume</div>
                      <div className="text-sm sm:text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
                        {tokenData.market_data.volume_24h_usd ?
                          parseFloat(tokenData.market_data.volume_24h_usd) >= 1000000 ?
                            `$${(parseFloat(tokenData.market_data.volume_24h_usd) / 1000000).toFixed(1)}M` :
                            parseFloat(tokenData.market_data.volume_24h_usd) >= 1000 ?
                              `$${(parseFloat(tokenData.market_data.volume_24h_usd) / 1000).toFixed(1)}K` :
                              `$${parseFloat(tokenData.market_data.volume_24h_usd).toFixed(0)}`
                          : 'N/A'
                        }
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">1h Change</div>
                      <div className={`text-sm sm:text-lg md:text-xl font-bold flex items-center ${
                        (parseFloat(tokenData.market_data.price_change_1h) || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {(parseFloat(tokenData.market_data.price_change_1h) || 0) >= 0 ?
                          <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" /> :
                          <ArrowDownRight className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        }
                        <span className="truncate">{(parseFloat(tokenData.market_data.price_change_1h) || 0).toFixed(2)}%</span>
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">24h Change</div>
                      <div className={`text-sm sm:text-lg md:text-xl font-bold flex items-center ${
                        (parseFloat(tokenData.market_data.price_change_24h) || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {(parseFloat(tokenData.market_data.price_change_24h) || 0) >= 0 ?
                          <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" /> :
                          <ArrowDownRight className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        }
                        <span className="truncate">{(parseFloat(tokenData.market_data.price_change_24h) || 0).toFixed(2)}%</span>
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700 col-span-2 sm:col-span-1">
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">Holders</div>
                      <div className="text-sm sm:text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
                        {tokenData.market_data.holders_count ?
                          (tokenData.market_data.holders_count > 1000 ?
                            (tokenData.market_data.holders_count / 1000).toFixed(1) + 'K' :
                            tokenData.market_data.holders_count.toLocaleString()
                          ) : 'N/A'
                        }
                      </div>
                      {tokenData.market_data.holders_count && tokenData.market_data.holders_count < 100 && (
                        <div className="text-[10px] sm:text-xs text-gray-500 mt-1">Limited data</div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Fallback static data - Mobile Responsive Grid
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                    <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">Market Cap</div>
                      <div className="text-sm sm:text-lg md:text-xl font-bold text-gray-400">N/A</div>
                      <div className="text-[10px] sm:text-xs text-gray-500">No market data available</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">Current Price</div>
                      <div className="text-sm sm:text-lg md:text-xl font-bold text-gray-400">N/A</div>
                      <div className="text-[10px] sm:text-xs text-gray-500">No market data available</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700 col-span-2 sm:col-span-1">
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">24h Volume</div>
                      <div className="text-sm sm:text-lg md:text-xl font-bold text-gray-400">N/A</div>
                      <div className="text-[10px] sm:text-xs text-gray-500">No market data available</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">1h Change</div>
                      <div className="text-sm sm:text-lg md:text-xl font-bold text-gray-400">N/A</div>
                      <div className="text-[10px] sm:text-xs text-gray-500">No market data available</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">24h Change</div>
                      <div className="text-sm sm:text-lg md:text-xl font-bold text-gray-400">N/A</div>
                      <div className="text-[10px] sm:text-xs text-gray-500">No market data available</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700 col-span-2 sm:col-span-1">
                      <div className="text-xs sm:text-sm text-gray-500 mb-1">Holders</div>
                      <div className="text-sm sm:text-lg md:text-xl font-bold text-gray-400">N/A</div>
                      <div className="text-[10px] sm:text-xs text-gray-500">No market data available</div>
                    </div>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        </div>
      </main>

      {/* Autopilot Progress Modal */}
      {showAutopilotModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                  <Rocket className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Activating Autopilot</h3>
                  <p className="text-purple-100 text-sm">Setting up automated trading system...</p>
                </div>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-8">
              {autopilotProgress.step === 'error' ? (
                /* Error State */
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Activation Failed
                  </h4>
                  <p className="text-red-600 dark:text-red-400 mb-6">
                    {autopilotProgress.message}
                  </p>
                  <button
                    onClick={() => setShowAutopilotModal(false)}
                    className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-6 py-2 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : autopilotProgress.step === 'complete' ? (
                /* Success State */
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Autopilot Activated!
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {autopilotProgress.message}
                  </p>
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
                    <p className="text-sm text-green-800 dark:text-green-200 font-medium mb-2">
                      🚀 System Summary:
                    </p>
                    <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
                      <li>✅ Created 10 trading wallets</li>
                      <li>✅ Distributed SOL and USDC</li>
                      <li>✅ Set up automated agent</li>
                      <li>✅ Started trading strategy</li>
                    </ul>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    Redirecting to agent dashboard in 2 seconds...
                  </p>
                </div>
              ) : (
                /* Progress State */
                <div>
                  <div className="mb-6">
                    <div className="flex items-center mb-4">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {autopilotProgress.message}
                          </span>
                          <span className="text-sm font-medium text-purple-600">
                            {autopilotProgress.progress}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-indigo-500 h-full rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${autopilotProgress.progress}%` }}
                          >
                            <div className="h-full bg-white/20 animate-pulse"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress Steps */}
                  <div className="space-y-3">
                    {[
                      { step: 'checking', label: 'Verifying conditions', icon: Shield },
                      { step: 'wallets', label: 'Creating wallets', icon: Users },
                      { step: 'sol', label: 'Distributing SOL', icon: DollarSign },
                      { step: 'usdc', label: 'Distributing USDC', icon: DollarSign },
                      { step: 'tokens', label: 'Distributing tokens', icon: Settings },
                      { step: 'agent', label: 'Creating agent', icon: Bot },
                      { step: 'starting', label: 'Starting strategy', icon: Play },
                      { step: 'complete', label: 'Complete!', icon: CheckCircle }
                    ].map(({ step, label, icon: Icon }) => {
                      const isActive = autopilotProgress.step === step;
                      const isCompleted = ['checking', 'wallets', 'group', 'sol', 'usdc', 'tokens', 'agent', 'starting'].indexOf(step) < 
                                        ['checking', 'wallets', 'group', 'sol', 'usdc', 'tokens', 'agent', 'starting', 'complete'].indexOf(autopilotProgress.step);
                      
                      return (
                        <div key={step} className={`flex items-center p-2 rounded-lg transition-colors ${
                          isActive ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800' : 
                          isCompleted ? 'bg-green-50 dark:bg-green-900/20' : 
                          'bg-gray-50 dark:bg-gray-800'
                        }`}>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                            isActive ? 'bg-purple-500' : 
                            isCompleted ? 'bg-green-500' : 
                            'bg-gray-300 dark:bg-gray-600'
                          }`}>
                            {isCompleted ? (
                              <Check className="w-4 h-4 text-white" />
                            ) : (
                              <Icon className={`w-4 h-4 ${
                                isActive ? 'text-white animate-pulse' : 'text-gray-500'
                              }`} />
                            )}
                          </div>
                          <span className={`text-sm font-medium ${
                            isActive ? 'text-purple-700 dark:text-purple-300' : 
                            isCompleted ? 'text-green-700 dark:text-green-300' : 
                            'text-gray-500 dark:text-gray-400'
                          }`}>
                            {label}
                          </span>
                          {isActive && (
                            <div className="ml-auto">
                              <RefreshCw className="w-4 h-4 text-purple-500 animate-spin" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-6 text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      This may take a few moments. Please do not close this window.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Professional Confirmation Modal */}
      {showConfirmationModal && confirmationData && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-5">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mr-3">
                  <Rocket className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{confirmationData.title}</h3>
                  <p className="text-purple-100 text-sm">{confirmationData.message}</p>
                </div>
              </div>
            </div>
            
            {/* Body */}
            <div className="p-3 sm:p-4 md:p-6">
              <div className="space-y-3 mb-6">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  This will perform the following actions:
                </p>
                {confirmationData.details.map((detail, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mt-0.5">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{detail}</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3 mb-6">
                <p className="text-xs text-purple-800 dark:text-purple-200 flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  This process is fully automated and may take several minutes to complete.
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={confirmationData.onCancel}
                  className="flex-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 py-2.5 px-4 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmationData.onConfirm}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-2.5 px-4 rounded-lg font-bold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg"
                >
                  Activate Autopilot
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Professional Requirements Modal */}
      {showRequirementsModal && requirementsData && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-pink-500 px-6 py-5">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mr-3">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{requirementsData.title}</h3>
                  <p className="text-red-100 text-sm">{requirementsData.message}</p>
                </div>
              </div>
            </div>
            
            {/* Body */}
            <div className="p-3 sm:p-4 md:p-6">
              <div className="space-y-4 mb-6">
                {requirementsData.requirements.map((req, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        req.status 
                          ? 'bg-green-100 dark:bg-green-900/30' 
                          : 'bg-red-100 dark:bg-red-900/30'
                      }`}>
                        {req.status ? (
                          <CheckCircle className="w-3 h-3 text-green-600" />
                        ) : (
                          <X className="w-3 h-3 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {req.item}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Required: {req.required}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${
                        req.status 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {req.current}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-6">
                <p className="text-xs text-blue-800 dark:text-blue-200 flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  Ensure your wallet meets all requirements before attempting to activate autopilot.
                </p>
              </div>
              
              {/* Actions */}
              <div className="text-center">
                <button
                  onClick={() => setShowRequirementsModal(false)}
                  className="w-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 py-2.5 px-4 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Understood
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Professional Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-pink-500 px-6 py-5">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mr-3">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Error</h3>
                  <p className="text-red-100 text-sm">Something went wrong</p>
                </div>
              </div>
            </div>
            
            {/* Body */}
            <div className="p-3 sm:p-4 md:p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                {errorMessage}
              </p>
              
              {/* Actions */}
              <div className="text-center">
                <button
                  onClick={() => setShowErrorModal(false)}
                  className="w-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 py-2.5 px-4 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default IPTokenDashboard;